import { useState, useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import {
  Wrench,
  Search,
  Plus,
  Trash2,
  Edit2,
  ExternalLink,
  Package,
  ShieldCheck,
  Loader2,
  Check,
  AlertCircle,
  Filter,
  RotateCcw,
  Layers,
  Car,
  Tag,
  CheckCircle2,
  HelpCircle,
  Sparkles,
  SlidersHorizontal,
  Star,
} from 'lucide-react'
import {
  useParts,
  useCreatePart,
  useUpdatePart,
  useDeletePart,
  useToggleFeaturedPart,
} from '@/modules/parts/hooks/use-parts'
import type { PartItem } from '@/modules/parts/services/parts.service'
import { Link } from 'react-router-dom'
import {
  PARTS_CATEGORIES_DATA,
  PART_CONDITIONS,
  POPULAR_VEHICLE_BRANDS,
  VEHICLE_TYPES,
  AVAILABILITY_STATUSES,
  POPULAR_YEARS,
  ENGINE_VARIANTS,
} from '@/constants/parts-taxonomy'
import { toast } from 'sonner'

export function AdminPartsPage() {
  // ── Filters State ────────────────────────────────────────────────────────
  const [search, setSearch] = useState('')
  const [conditionFilter, setConditionFilter] = useState('ALL')
  const [categoryFilter, setCategoryFilter] = useState('ALL')
  const [subCategoryFilter, setSubCategoryFilter] = useState('ALL')
  const [brandFilter, setBrandFilter] = useState('ALL')
  const [modelFilter, setModelFilter] = useState('ALL')
  const [availabilityFilter, setAvailabilityFilter] = useState('ALL')
  const [featuredFilter, setFeaturedFilter] = useState<'ALL' | 'FEATURED_ONLY' | 'NOT_FEATURED'>('ALL')
  const [minPrice, setMinPrice] = useState('')
  const [maxPrice, setMaxPrice] = useState('')
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false)

  // ── Modal State ──────────────────────────────────────────────────────────
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [editingPart, setEditingPart] = useState<PartItem | null>(null)
  const [deletePartId, setDeletePartId] = useState<number | null>(null)

  // ── Form State ───────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    title: '',
    partNumber: '',
    category: PARTS_CATEGORIES_DATA[0].name,
    subCategory: PARTS_CATEGORIES_DATA[0].subParts[0],
    brand: 'Maruti Genuine Parts',
    vehicleBrand: 'Maruti Suzuki',
    vehicleModel: 'Swift',
    vehicleYear: '2019',
    engineVariant: '1.2L Petrol',
    vehicleType: 'CAR',
    condition: 'NEW',
    availability: 'IN_STOCK',
    isFeatured: false,
    price: '',
    stockCount: '10',
    isOEM: true,
    warranty: '1 Year Manufacturer Warranty',
    shippingInfo: 'Express Freight (2-3 Business Days)',
    compatibility: 'Maruti Suzuki Swift (2018-2024)',
    imageUrl: 'https://images.unsplash.com/photo-1486006920555-c77dce18193b?auto=format&fit=crop&w=800&q=80',
    description: 'Precision engineered automotive component meeting original equipment standards.',
  })
  const [formError, setFormError] = useState('')

  // ── Data & Mutations ─────────────────────────────────────────────────────
  const { data: partsData, isLoading } = useParts({ limit: 150 })
  const createPartMutation = useCreatePart()
  const updatePartMutation = useUpdatePart()
  const deletePartMutation = useDeletePart()
  const toggleFeaturedMutation = useToggleFeaturedPart()

  const allParts = partsData?.items || []

  // Dynamic models based on selected brand in filter
  const selectedBrandModels = useMemo(() => {
    if (brandFilter === 'ALL') return []
    const found = POPULAR_VEHICLE_BRANDS.find((b) => b.brand.toLowerCase() === brandFilter.toLowerCase())
    return found ? found.models : []
  }, [brandFilter])

  // Subparts based on category filter
  const availableSubPartsForFilter = useMemo(() => {
    if (categoryFilter === 'ALL') return []
    const cat = PARTS_CATEGORIES_DATA.find((c) => c.name.toLowerCase() === categoryFilter.toLowerCase())
    return cat ? cat.subParts : []
  }, [categoryFilter])

  // Subparts for Form modal based on formData.category
  const formSubParts = useMemo(() => {
    const cat = PARTS_CATEGORIES_DATA.find((c) => c.name.toLowerCase() === formData.category.toLowerCase())
    return cat ? cat.subParts : PARTS_CATEGORIES_DATA[0].subParts
  }, [formData.category])

  // Models for Form modal based on formData.vehicleBrand
  const formBrandModels = useMemo(() => {
    const found = POPULAR_VEHICLE_BRANDS.find((b) => b.brand.toLowerCase() === formData.vehicleBrand.toLowerCase())
    return found ? found.models : ['Standard Model']
  }, [formData.vehicleBrand])

  // Stats Summary
  const totalCount = allParts.length
  const featuredCount = allParts.filter((p) => p.isFeatured).length
  const inStockCount = allParts.filter((p) => (p.availability === 'IN_STOCK' || p.stockCount > 0)).length
  const oemCount = allParts.filter((p) => p.isOEM).length

  // Filter count
  const activeFiltersCount = [
    conditionFilter !== 'ALL',
    categoryFilter !== 'ALL',
    subCategoryFilter !== 'ALL',
    brandFilter !== 'ALL',
    modelFilter !== 'ALL',
    availabilityFilter !== 'ALL',
    featuredFilter !== 'ALL',
    Boolean(minPrice),
    Boolean(maxPrice),
    Boolean(search),
  ].filter(Boolean).length

  const handleResetFilters = () => {
    setSearch('')
    setConditionFilter('ALL')
    setCategoryFilter('ALL')
    setSubCategoryFilter('ALL')
    setBrandFilter('ALL')
    setModelFilter('ALL')
    setAvailabilityFilter('ALL')
    setFeaturedFilter('ALL')
    setMinPrice('')
    setMaxPrice('')
  }

  // ── Filtered Parts Logic ─────────────────────────────────────────────────
  const filteredParts = useMemo(() => {
    return allParts.filter((part) => {
      // 1. Search Query
      if (search) {
        const q = search.toLowerCase()
        const matchTitle = part.title?.toLowerCase().includes(q)
        const matchPartNo = part.partNumber?.toLowerCase().includes(q)
        const matchBrand = part.brand?.toLowerCase().includes(q)
        const matchVBrand = part.vehicleBrand?.toLowerCase().includes(q)
        const matchVModel = part.vehicleModel?.toLowerCase().includes(q)
        const matchSub = part.subCategory?.toLowerCase().includes(q)
        if (!matchTitle && !matchPartNo && !matchBrand && !matchVBrand && !matchVModel && !matchSub) {
          return false
        }
      }

      // 2. Condition Filter (NEW, USED, REFURBISHED)
      if (conditionFilter !== 'ALL') {
        const cond = (part.condition || 'NEW').toUpperCase()
        if (conditionFilter === 'NEW' && !cond.includes('NEW')) return false
        if (conditionFilter === 'USED' && !cond.includes('USED') && !cond.includes('PRE-OWNED')) return false
        if (conditionFilter === 'REFURBISHED' && !cond.includes('REFURBISHED')) return false
      }

      // 3. Category Filter
      if (categoryFilter !== 'ALL' && part.category?.toLowerCase() !== categoryFilter.toLowerCase()) {
        return false
      }

      // 4. Sub-category Filter
      if (subCategoryFilter !== 'ALL') {
        const sub = part.subCategory || (part.specifications as any)?.subCategory || ''
        if (sub.toLowerCase() !== subCategoryFilter.toLowerCase()) return false
      }

      // 5. Vehicle Brand
      if (brandFilter !== 'ALL') {
        const vb = part.vehicleBrand || (part.specifications as any)?.vehicleBrand || part.brand || ''
        const comp = Array.isArray(part.compatibility) ? part.compatibility.join(' ') : ''
        if (!vb.toLowerCase().includes(brandFilter.toLowerCase()) && !comp.toLowerCase().includes(brandFilter.toLowerCase())) {
          return false
        }
      }

      // 6. Vehicle Model
      if (modelFilter !== 'ALL') {
        const vm = part.vehicleModel || (part.specifications as any)?.vehicleModel || ''
        const comp = Array.isArray(part.compatibility) ? part.compatibility.join(' ') : ''
        if (!vm.toLowerCase().includes(modelFilter.toLowerCase()) && !comp.toLowerCase().includes(modelFilter.toLowerCase())) {
          return false
        }
      }

      // 7. Availability Filter
      if (availabilityFilter !== 'ALL') {
        const avail = part.availability || (part.specifications as any)?.availability || (part.stockCount > 0 ? 'IN_STOCK' : 'ENQUIRY_REQUIRED')
        if (availabilityFilter === 'IN_STOCK' && avail !== 'IN_STOCK') return false
        if (availabilityFilter === 'ENQUIRY_REQUIRED' && avail !== 'ENQUIRY_REQUIRED') return false
      }

      // 8. Featured Filter
      if (featuredFilter === 'FEATURED_ONLY' && !part.isFeatured) return false
      if (featuredFilter === 'NOT_FEATURED' && part.isFeatured) return false

      // 9. Min / Max Price
      const priceNum = typeof part.price === 'number' ? part.price : Number(String(part.price).replace(/[^0-9.]/g, '')) || 0
      if (minPrice && priceNum < Number(minPrice)) return false
      if (maxPrice && priceNum > Number(maxPrice)) return false

      return true
    })
  }, [allParts, search, conditionFilter, categoryFilter, subCategoryFilter, brandFilter, modelFilter, availabilityFilter, featuredFilter, minPrice, maxPrice])

  // ── Open Modals ──────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setEditingPart(null)
    setFormData({
      title: '',
      partNumber: `OEM-${Math.floor(100000 + Math.random() * 900000)}`,
      category: PARTS_CATEGORIES_DATA[0].name,
      subCategory: PARTS_CATEGORIES_DATA[0].subParts[0],
      brand: 'Maruti Genuine Parts',
      vehicleBrand: 'Maruti Suzuki',
      vehicleModel: 'Swift',
      vehicleYear: '2019',
      engineVariant: '1.2L Petrol',
      vehicleType: 'CAR',
      condition: 'NEW',
      availability: 'IN_STOCK',
      isFeatured: false,
      price: '',
      stockCount: '10',
      isOEM: true,
      warranty: '1 Year Manufacturer Warranty',
      shippingInfo: 'Express Freight (2-3 Business Days)',
      compatibility: 'Maruti Suzuki Swift (2018-2024)',
      imageUrl: 'https://images.unsplash.com/photo-1486006920555-c77dce18193b?auto=format&fit=crop&w=800&q=80',
      description: 'Precision engineered automotive component meeting original equipment standards.',
    })
    setFormError('')
    setIsAddOpen(true)
  }

  const openEditModal = (part: PartItem) => {
    setEditingPart(part)
    const specs = (part.specifications as any) || {}
    setFormData({
      title: part.title,
      partNumber: part.partNumber,
      category: part.category || PARTS_CATEGORIES_DATA[0].name,
      subCategory: part.subCategory || specs.subCategory || '',
      brand: part.brand || 'Genuine Parts',
      vehicleBrand: part.vehicleBrand || specs.vehicleBrand || 'Maruti Suzuki',
      vehicleModel: part.vehicleModel || specs.vehicleModel || 'Swift',
      vehicleYear: String(part.vehicleYear || specs.vehicleYear || '2019'),
      engineVariant: part.engineVariant || specs.engineVariant || '1.2L Petrol',
      vehicleType: part.vehicleType || specs.vehicleType || 'CAR',
      condition: part.condition || 'NEW',
      availability: part.availability || specs.availability || (part.stockCount > 0 ? 'IN_STOCK' : 'ENQUIRY_REQUIRED'),
      isFeatured: Boolean(part.isFeatured || specs.isFeatured),
      price: String(part.price),
      stockCount: String(part.stockCount),
      isOEM: Boolean(part.isOEM),
      warranty: part.warranty || '1 Year Manufacturer Warranty',
      shippingInfo: part.shippingInfo || 'Express Freight (2-3 Business Days)',
      compatibility: Array.isArray(part.compatibility) ? part.compatibility.join(', ') : '',
      imageUrl: Array.isArray(part.images) && part.images.length > 0
        ? (typeof part.images[0] === 'string' ? part.images[0] : (part.images[0] as any)?.url || '')
        : '',
      description: part.description || '',
    })
    setFormError('')
    setIsAddOpen(true)
  }

  const handleToggleFeatured = async (part: PartItem) => {
    try {
      await toggleFeaturedMutation.mutateAsync(part.id)
      toast.success(
        part.isFeatured
          ? `"${part.title}" removed from Featured Parts`
          : `⭐ "${part.title}" is now marked as Featured on Homepage & Store!`
      )
    } catch (err: any) {
      toast.error('Failed to update featured status.')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (!formData.title || !formData.partNumber || !formData.brand || !formData.price) {
      setFormError('Please fill in all required fields (Title, Part #, Brand, Price).')
      return
    }

    const payload = {
      title: formData.title,
      partNumber: formData.partNumber,
      category: formData.category,
      subCategory: formData.subCategory,
      brand: formData.brand,
      vehicleBrand: formData.vehicleBrand,
      vehicleModel: formData.vehicleModel,
      vehicleYear: Number(formData.vehicleYear) || 2019,
      engineVariant: formData.engineVariant,
      vehicleType: formData.vehicleType,
      availability: formData.availability,
      isFeatured: formData.isFeatured,
      price: parseFloat(formData.price) || 0,
      stockCount: parseInt(formData.stockCount, 10) || 0,
      inStock: formData.availability === 'IN_STOCK' && (parseInt(formData.stockCount, 10) || 0) > 0,
      isOEM: formData.isOEM,
      condition: formData.condition,
      warranty: formData.warranty,
      shippingInfo: formData.shippingInfo,
      compatibility: formData.compatibility ? formData.compatibility.split(',').map((s) => s.trim()).filter(Boolean) : [`${formData.vehicleBrand} ${formData.vehicleModel}`],
      specifications: {
        subCategory: formData.subCategory,
        vehicleBrand: formData.vehicleBrand,
        vehicleModel: formData.vehicleModel,
        vehicleYear: formData.vehicleYear,
        engineVariant: formData.engineVariant,
        vehicleType: formData.vehicleType,
        availability: formData.availability,
        isFeatured: formData.isFeatured,
      },
      images: formData.imageUrl ? [formData.imageUrl] : [],
      description: formData.description,
    }

    try {
      if (editingPart) {
        await updatePartMutation.mutateAsync({ id: editingPart.id, data: payload })
        toast.success('Part listing updated successfully!')
      } else {
        await createPartMutation.mutateAsync(payload)
        toast.success('New part published to catalog!')
      }
      setIsAddOpen(false)
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to save part listing.')
    }
  }

  const handleDelete = async () => {
    if (!deletePartId) return
    try {
      await deletePartMutation.mutateAsync(deletePartId)
      setDeletePartId(null)
      toast.success('Part listing deleted.')
    } catch (err) {
      console.error(err)
    }
  }

  // Helper for Condition Badge
  const renderConditionBadge = (condition?: string) => {
    const c = (condition || 'NEW').toUpperCase()
    if (c.includes('NEW')) {
      return (
        <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-[10px] font-bold">
          New
        </Badge>
      )
    }
    if (c.includes('REFURBISHED')) {
      return (
        <Badge className="bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20 text-[10px] font-bold">
          Refurbished
        </Badge>
      )
    }
    return (
      <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-[10px] font-bold">
        Used / Tested
      </Badge>
    )
  }

  return (
    <div className="space-y-6">
      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">
              Car Spare Parts Management
            </h1>
            <Badge className="bg-[#CF2F2F]/15 text-[#CF2F2F] border border-[#CF2F2F]/30 font-bold uppercase text-[10px]">
              Master Inventory
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Configure auto components, OEM numbers, vehicle compatibility, conditions, and featured parts.
          </p>
        </div>

        <Button
          onClick={openCreateModal}
          className="bg-primary text-white hover:bg-primary/90 glow-sm font-bold gap-2 shrink-0 cursor-pointer"
        >
          <Plus className="size-4" /> Add New Part
        </Button>
      </div>

      {/* ── KPI Summary Cards ────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-border/80 bg-card p-4 rounded-xl shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Parts</span>
            <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Package className="size-4" />
            </div>
          </div>
          <p className="font-display text-2xl font-extrabold text-foreground mt-2">{totalCount}</p>
          <span className="text-[11px] text-muted-foreground">Across 11 categories</span>
        </Card>

        <Card className="border border-border/80 bg-card p-4 rounded-xl shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Featured Parts</span>
            <div className="size-8 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center">
              <Star className="size-4 fill-amber-400" />
            </div>
          </div>
          <p className="font-display text-2xl font-extrabold text-amber-500 mt-2">{featuredCount}</p>
          <span className="text-[11px] text-muted-foreground font-semibold">Active on Homepage</span>
        </Card>

        <Card className="border border-border/80 bg-card p-4 rounded-xl shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">In Stock (Direct Buy)</span>
            <div className="size-8 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
              <CheckCircle2 className="size-4" />
            </div>
          </div>
          <p className="font-display text-2xl font-extrabold text-foreground mt-2">{inStockCount}</p>
          <span className="text-[11px] text-emerald-600 font-semibold">Immediate cart delivery</span>
        </Card>

        <Card className="border border-border/80 bg-card p-4 rounded-xl shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Genuine OEM Certified</span>
            <div className="size-8 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
              <ShieldCheck className="size-4" />
            </div>
          </div>
          <p className="font-display text-2xl font-extrabold text-foreground mt-2">{oemCount}</p>
          <span className="text-[11px] text-muted-foreground">Manufacturer guaranteed</span>
        </Card>
      </div>

      {/* ── Comprehensive Filter Control Bar ─────────────────────────────── */}
      <Card className="p-4 rounded-xl border border-border/80 bg-card/90 shadow-xs space-y-3">
        {/* Row 1: Primary Search + Quick Filters */}
        <div className="flex flex-col md:flex-row items-center gap-3">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
            <Input
              placeholder="Search parts by title, OEM / part #, brand, or model..."
              className="pl-9 bg-background text-xs font-medium"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            {/* Featured Only Filter Toggle */}
            <Button
              type="button"
              variant={featuredFilter === 'FEATURED_ONLY' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFeaturedFilter(featuredFilter === 'FEATURED_ONLY' ? 'ALL' : 'FEATURED_ONLY')}
              className={`gap-1.5 text-xs font-bold cursor-pointer ${
                featuredFilter === 'FEATURED_ONLY'
                  ? 'bg-amber-500 text-black hover:bg-amber-400'
                  : 'text-amber-500 border-amber-500/40 hover:bg-amber-500/10'
              }`}
            >
              <Star className={`size-3.5 ${featuredFilter === 'FEATURED_ONLY' ? 'fill-black' : 'fill-amber-400 text-amber-400'}`} />
              <span>Featured ({featuredCount})</span>
            </Button>

            {/* Condition Filter */}
            <select
              value={conditionFilter}
              onChange={(e) => setConditionFilter(e.target.value)}
              className="h-9 rounded-lg border border-border bg-background px-3 text-xs font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
            >
              <option value="ALL">All Conditions</option>
              <option value="NEW">New</option>
              <option value="USED">Used / Tested</option>
              <option value="REFURBISHED">Refurbished</option>
            </select>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value)
                setSubCategoryFilter('ALL')
              }}
              className="h-9 rounded-lg border border-border bg-background px-3 text-xs font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-primary max-w-[160px] truncate cursor-pointer"
            >
              <option value="ALL">All 11 Categories</option>
              {PARTS_CATEGORIES_DATA.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>

            {/* Availability Filter */}
            <select
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value)}
              className="h-9 rounded-lg border border-border bg-background px-3 text-xs font-semibold text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
            >
              <option value="ALL">All Stock</option>
              <option value="IN_STOCK">In Stock</option>
              <option value="ENQUIRY_REQUIRED">Enquiry Required</option>
            </select>

            {/* Toggle Advanced Filters */}
            <Button
              type="button"
              variant={isAdvancedFiltersOpen ? 'default' : 'outline'}
              size="sm"
              onClick={() => setIsAdvancedFiltersOpen(!isAdvancedFiltersOpen)}
              className="gap-1.5 text-xs font-bold cursor-pointer"
            >
              <SlidersHorizontal className="size-3.5" />
              <span>Filters</span>
              {activeFiltersCount > 0 && (
                <span className="flex size-4 items-center justify-center rounded-full bg-primary text-[10px] text-white">
                  {activeFiltersCount}
                </span>
              )}
            </Button>

            {activeFiltersCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className="text-xs text-muted-foreground hover:text-foreground cursor-pointer"
                title="Reset all filters"
              >
                <RotateCcw className="size-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Row 2: Secondary / Advanced Filters Drawer */}
        {isAdvancedFiltersOpen && (
          <div className="pt-3 border-t border-border/60 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {/* Sub-part Filter */}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">
                Sub-Part Component
              </label>
              <select
                value={subCategoryFilter}
                onChange={(e) => setSubCategoryFilter(e.target.value)}
                disabled={categoryFilter === 'ALL'}
                className="w-full h-8 rounded-lg border border-border bg-background px-2.5 text-xs font-medium text-foreground disabled:opacity-50 cursor-pointer"
              >
                <option value="ALL">All Sub-parts</option>
                {availableSubPartsForFilter.map((sub) => (
                  <option key={sub} value={sub}>
                    {sub}
                  </option>
                ))}
              </select>
            </div>

            {/* Vehicle Brand */}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">
                Vehicle Brand
              </label>
              <select
                value={brandFilter}
                onChange={(e) => {
                  setBrandFilter(e.target.value)
                  setModelFilter('ALL')
                }}
                className="w-full h-8 rounded-lg border border-border bg-background px-2.5 text-xs font-medium text-foreground cursor-pointer"
              >
                <option value="ALL">All Vehicle Brands</option>
                {POPULAR_VEHICLE_BRANDS.map((b) => (
                  <option key={b.brand} value={b.brand}>
                    {b.brand}
                  </option>
                ))}
              </select>
            </div>

            {/* Vehicle Model */}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">
                Vehicle Model
              </label>
              <select
                value={modelFilter}
                onChange={(e) => setModelFilter(e.target.value)}
                disabled={brandFilter === 'ALL'}
                className="w-full h-8 rounded-lg border border-border bg-background px-2.5 text-xs font-medium text-foreground disabled:opacity-50 cursor-pointer"
              >
                <option value="ALL">All Models</option>
                {selectedBrandModels.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            {/* Price Range */}
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block mb-1">
                Price Range (₹)
              </label>
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  placeholder="Min"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="h-8 text-xs px-2"
                />
                <span className="text-muted-foreground text-xs">-</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="h-8 text-xs px-2"
                />
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* ── Parts Table ──────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="p-16 text-center flex flex-col items-center justify-center space-y-3">
          <Loader2 className="size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading parts catalog from database...</p>
        </div>
      ) : filteredParts.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-border flex flex-col items-center justify-center space-y-3">
          <Package className="size-12 text-muted-foreground/40" />
          <h3 className="font-display text-lg font-bold">No Matching Parts Found</h3>
          <p className="text-xs text-muted-foreground max-w-sm">
            {activeFiltersCount > 0
              ? 'Try resetting some of your search filters to view more items.'
              : 'Get started by listing your first auto part.'}
          </p>
          <div className="flex items-center gap-2 mt-2">
            {activeFiltersCount > 0 && (
              <Button onClick={handleResetFilters} variant="outline" size="sm">
                Reset Filters
              </Button>
            )}
            <Button onClick={openCreateModal} size="sm" className="bg-primary text-white font-bold cursor-pointer">
              <Plus className="mr-1.5 size-4" /> Add Part
            </Button>
          </div>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border/80 bg-card shadow-xs">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-muted/40 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Part Details</th>
                <th className="px-4 py-3">Featured Status</th>
                <th className="px-4 py-3">OEM / Part #</th>
                <th className="px-4 py-3">Category &amp; Sub-part</th>
                <th className="px-4 py-3">Vehicle Compatibility</th>
                <th className="px-4 py-3">Condition</th>
                <th className="px-4 py-3">Availability &amp; Stock</th>
                <th className="px-4 py-3">Price</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filteredParts.map((part) => {
                const img = Array.isArray(part.images) && part.images.length > 0
                  ? (typeof part.images[0] === 'string' ? part.images[0] : (part.images[0] as any)?.url || '')
                  : 'https://images.unsplash.com/photo-1486006920555-c77dce18193b?auto=format&fit=crop&w=200&q=80'

                const specs = (part.specifications as any) || {}
                const subPart = part.subCategory || specs.subCategory
                const vBrand = part.vehicleBrand || specs.vehicleBrand
                const vModel = part.vehicleModel || specs.vehicleModel
                const vYear = part.vehicleYear || specs.vehicleYear
                const availability = part.availability || specs.availability || (part.stockCount > 0 ? 'IN_STOCK' : 'ENQUIRY_REQUIRED')

                return (
                  <tr key={part.id} className="hover:bg-muted/20 transition-colors">
                    {/* 1. Part Title & Image */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={img}
                          alt={part.title}
                          className="h-12 w-12 rounded-lg object-cover border border-border/70 shrink-0 bg-muted"
                        />
                        <div className="space-y-0.5 min-w-0 max-w-xs">
                          <Link
                            to={`/parts/${part.slug}`}
                            target="_blank"
                            className="font-bold text-foreground hover:text-primary transition-colors flex items-center gap-1 line-clamp-1 text-xs"
                          >
                            <span>{part.title}</span>
                            <ExternalLink className="size-3 shrink-0 opacity-70" />
                          </Link>
                          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                            <span>{part.brand}</span>
                            {part.isOEM && (
                              <Badge className="bg-primary/10 text-primary border border-primary/20 text-[9px] px-1 py-0 font-bold">
                                OEM
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* 2. Featured Toggle Switch */}
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleToggleFeatured(part)}
                        disabled={toggleFeaturedMutation.isPending}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                          part.isFeatured
                            ? 'bg-amber-500/15 text-amber-500 border border-amber-500/30 hover:bg-amber-500/25 shadow-xs'
                            : 'bg-muted/40 text-muted-foreground border border-border/60 hover:text-foreground hover:bg-muted'
                        }`}
                        title={part.isFeatured ? 'Part is active on Homepage Featured Section. Click to unfeature.' : 'Click to feature this part on Homepage'}
                      >
                        <Star className={`size-3.5 ${part.isFeatured ? 'fill-amber-400 text-amber-400' : ''}`} />
                        <span>{part.isFeatured ? 'Featured' : 'Standard'}</span>
                      </button>
                    </td>

                    {/* 3. OEM / Part Number */}
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-foreground">
                      <span className="bg-muted/70 px-2 py-1 rounded border border-border/60">
                        {part.partNumber}
                      </span>
                    </td>

                    {/* 4. Category & Sub-part */}
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <Badge variant="outline" className="text-[10px] font-bold">
                          {part.category}
                        </Badge>
                        {subPart && (
                          <p className="text-[11px] text-muted-foreground font-medium truncate max-w-[140px]">
                            {subPart}
                          </p>
                        )}
                      </div>
                    </td>

                    {/* 5. Vehicle Compatibility */}
                    <td className="px-4 py-3">
                      {vBrand || vModel ? (
                        <div className="space-y-0.5 text-xs">
                          <p className="font-bold text-foreground">
                            {vBrand} {vModel}
                          </p>
                          <p className="text-[11px] text-muted-foreground font-mono">
                            {vYear ? `Year ${vYear}` : ''} {specs.engineVariant ? `• ${specs.engineVariant}` : ''}
                          </p>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {Array.isArray(part.compatibility) && part.compatibility.length > 0
                            ? part.compatibility[0]
                            : 'Universal Fit'}
                        </span>
                      )}
                    </td>

                    {/* 6. Condition */}
                    <td className="px-4 py-3">{renderConditionBadge(part.condition)}</td>

                    {/* 7. Availability & Stock */}
                    <td className="px-4 py-3">
                      {availability === 'ENQUIRY_REQUIRED' ? (
                        <Badge className="bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20 text-[10px] font-bold">
                          Enquiry Required
                        </Badge>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                            part.stockCount > 0
                              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                              : 'bg-red-500/10 text-red-500'
                          }`}
                        >
                          {part.stockCount > 0 ? `${part.stockCount} in stock` : 'Out of stock'}
                        </span>
                      )}
                    </td>

                    {/* 8. Price */}
                    <td className="px-4 py-3 font-display font-extrabold text-foreground text-sm">
                      ₹{typeof part.price === 'number' ? part.price.toLocaleString('en-IN') : part.price}
                    </td>

                    {/* 9. Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-muted-foreground hover:text-foreground cursor-pointer"
                          onClick={() => openEditModal(part)}
                          title="Edit Part"
                        >
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-red-500 hover:text-red-600 hover:bg-red-500/10 cursor-pointer"
                          onClick={() => setDeletePartId(part.id)}
                          title="Delete Part"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Part Modal ─────────────────────────────────────── */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold flex items-center gap-2">
              <Wrench className="size-5 text-primary" />
              <span>{editingPart ? 'Edit Automotive Part' : 'Add New Automotive Part'}</span>
            </DialogTitle>
          </DialogHeader>

          {formError && (
            <div className="flex items-center gap-2 p-3 text-xs bg-red-500/10 border border-red-500/20 text-red-500 rounded-lg">
              <AlertCircle className="size-4 shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5 pt-2">
            {/* ── Section 1: Taxonomy & Identification ── */}
            <div className="rounded-xl border border-border/80 bg-muted/20 p-4 space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
                <Tag className="size-3.5" /> 1. Category &amp; Part Identification
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Category Select (11 Categories) */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Parent Category *
                  </label>
                  <select
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium cursor-pointer"
                    value={formData.category}
                    onChange={(e) => {
                      const newCat = e.target.value
                      const catDef = PARTS_CATEGORIES_DATA.find((c) => c.name === newCat)
                      setFormData({
                        ...formData,
                        category: newCat,
                        subCategory: catDef ? catDef.subParts[0] : '',
                      })
                    }}
                  >
                    {PARTS_CATEGORIES_DATA.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sub-Category / Specific Component */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Specific Component / Sub-Part *
                  </label>
                  <select
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium cursor-pointer"
                    value={formData.subCategory}
                    onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
                  >
                    {formSubParts.map((sub) => (
                      <option key={sub} value={sub}>
                        {sub}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Title */}
                <div className="sm:col-span-2 space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Part Title / Description *
                  </label>
                  <Input
                    required
                    placeholder="e.g. High Performance Front Brake Pads Set"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>

                {/* OEM Number */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    OEM / Part Number (SKU) *
                  </label>
                  <Input
                    required
                    placeholder="e.g. 55810-77M00"
                    value={formData.partNumber}
                    onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
                  />
                </div>

                {/* Brand / Maker */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Part Manufacturer / Brand *
                  </label>
                  <Input
                    required
                    placeholder="e.g. Bosch, Brembo, Maruti Genuine Parts"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* ── Section 2: Vehicle Compatibility ── */}
            <div className="rounded-xl border border-border/80 bg-muted/20 p-4 space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
                <Car className="size-3.5" /> 2. Vehicle Compatibility Matrix
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Vehicle Type */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Vehicle Type
                  </label>
                  <select
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium cursor-pointer"
                    value={formData.vehicleType}
                    onChange={(e) => setFormData({ ...formData, vehicleType: e.target.value })}
                  >
                    {VEHICLE_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle Brand */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Vehicle Brand *
                  </label>
                  <select
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium cursor-pointer"
                    value={formData.vehicleBrand}
                    onChange={(e) => {
                      const newBrand = e.target.value
                      const found = POPULAR_VEHICLE_BRANDS.find((b) => b.brand === newBrand)
                      setFormData({
                        ...formData,
                        vehicleBrand: newBrand,
                        vehicleModel: found ? found.models[0] : 'Standard Model',
                      })
                    }}
                  >
                    {POPULAR_VEHICLE_BRANDS.map((b) => (
                      <option key={b.brand} value={b.brand}>
                        {b.brand}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Vehicle Model */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Vehicle Model *
                  </label>
                  <select
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium cursor-pointer"
                    value={formData.vehicleModel}
                    onChange={(e) => setFormData({ ...formData, vehicleModel: e.target.value })}
                  >
                    {formBrandModels.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Manufacturing Year */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Manufacturing Year
                  </label>
                  <select
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium cursor-pointer"
                    value={formData.vehicleYear}
                    onChange={(e) => setFormData({ ...formData, vehicleYear: e.target.value })}
                  >
                    {POPULAR_YEARS.map((y) => (
                      <option key={y} value={String(y)}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Engine Variant */}
                <div className="sm:col-span-2 space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Engine Variant
                  </label>
                  <select
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium cursor-pointer"
                    value={formData.engineVariant}
                    onChange={(e) => setFormData({ ...formData, engineVariant: e.target.value })}
                  >
                    {ENGINE_VARIANTS.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* ── Section 3: Condition, Stock & Pricing ── */}
            <div className="rounded-xl border border-border/80 bg-muted/20 p-4 space-y-4">
              <span className="text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
                <Package className="size-3.5" /> 3. Condition, Availability &amp; Pricing
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Condition */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Condition *
                  </label>
                  <select
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium cursor-pointer"
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                  >
                    <option value="NEW">New (Brand New / OEM)</option>
                    <option value="USED">Used / Tested</option>
                    <option value="REFURBISHED">Refurbished</option>
                  </select>
                </div>

                {/* Availability */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Availability Mode *
                  </label>
                  <select
                    className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs font-medium cursor-pointer"
                    value={formData.availability}
                    onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                  >
                    <option value="IN_STOCK">In Stock (Direct Checkout)</option>
                    <option value="ENQUIRY_REQUIRED">Enquiry Required (Quote Lead)</option>
                  </select>
                </div>

                {/* Price */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Price (INR ₹) *
                  </label>
                  <Input
                    type="number"
                    required
                    placeholder="e.g. 2400"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  />
                </div>

                {/* Stock Count */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Units in Stock
                  </label>
                  <Input
                    type="number"
                    value={formData.stockCount}
                    onChange={(e) => setFormData({ ...formData, stockCount: e.target.value })}
                  />
                </div>

                {/* Warranty */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Warranty
                  </label>
                  <Input
                    placeholder="e.g. 1 Year Replacement"
                    value={formData.warranty}
                    onChange={(e) => setFormData({ ...formData, warranty: e.target.value })}
                  />
                </div>

                {/* Shipping Info */}
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    Shipping Policy
                  </label>
                  <Input
                    placeholder="e.g. Dispatch in 24-48 Hours"
                    value={formData.shippingInfo}
                    onChange={(e) => setFormData({ ...formData, shippingInfo: e.target.value })}
                  />
                </div>

                {/* OEM Checkbox */}
                <div className="sm:col-span-3 flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="isOEM"
                    checked={formData.isOEM}
                    onChange={(e) => setFormData({ ...formData, isOEM: e.target.checked })}
                    className="rounded border-input text-primary focus:ring-primary size-4 cursor-pointer"
                  />
                  <label htmlFor="isOEM" className="text-xs font-semibold text-foreground cursor-pointer select-none">
                    Certified Genuine OEM Factory Part (Official Manufacturer Fitment)
                  </label>
                </div>

                {/* Featured Part Checkbox */}
                <div className="sm:col-span-3 flex items-center gap-2 pt-2 border-t border-border/60 mt-1">
                  <input
                    type="checkbox"
                    id="isFeatured"
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                    className="rounded border-input text-amber-500 focus:ring-amber-500 size-4 cursor-pointer"
                  />
                  <label htmlFor="isFeatured" className="text-xs font-bold text-foreground cursor-pointer flex items-center gap-1.5 select-none">
                    <Star className="size-3.5 fill-amber-400 text-amber-400" />
                    Feature this Part on Homepage &amp; Storefront Highlights (Only featured parts display in Featured section)
                  </label>
                </div>
              </div>
            </div>

            {/* ── Section 4: Image & Description ── */}
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Product Image URL
                </label>
                <Input
                  placeholder="https://images.unsplash.com/..."
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Engineering Description &amp; Fitment Notes
                </label>
                <textarea
                  className="w-full min-h-[70px] rounded-lg border border-input bg-background p-2.5 text-xs text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Technical specifications, material grade, and vehicle compatibility details..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setIsAddOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createPartMutation.isPending || updatePartMutation.isPending}
                className="bg-primary text-white font-bold cursor-pointer"
              >
                {(createPartMutation.isPending || updatePartMutation.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {editingPart ? 'Save Changes' : 'Publish Part to Catalog'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      <Dialog open={Boolean(deletePartId)} onOpenChange={() => setDeletePartId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-bold">Delete Part Listing</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this part from the catalog? This will remove the listing from customer storefront and active carts.
          </p>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setDeletePartId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deletePartMutation.isPending}
              onClick={handleDelete}
            >
              {deletePartMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
